import { MouseEvent } from "react";
import { classColors } from "@/utils/classification-mapping";
import { useModalStore } from "@/stores/use-modal-store";
import { useEditorStore } from "../stores/use-editor-store";
import { Region } from "@/api/studio/data/types/region.type";
import { StudyType } from "@/api/studio/studies/types/study.type";
import { UpdateRegionModal } from "../modals/update-region-modal";
import { SceneColorPickerModal } from "../modals/scene-color-picker-modal";
import { BarsArrowUpIcon, ChevronDownIcon, DocumentTextIcon, ClipboardDocumentIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/ui/button";
import { EditElement } from "./edit-element";
import { useSegment } from "@/hooks/use-segment";
import { SetVisualHierarchyModal } from "../modals/set-visual-hierarchy-modal";
import { getUniqueLabel } from "@/features/studies/utils/get-unique-label";
import { processRegionsForPaste } from "@/features/studies/utils/paste-regions";
import { useClipboardRegionCheck } from "@/features/studies/hooks/use-clipboard-region-check";
import PAGraphic from "@/assets/study-assets/set-vh-graphic.png";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { toast } from "@/components/ui/toast";
import { cn } from "@/utils/classes";

/* Prop Types */
interface RegionListProps {
  projectId: string; // Used for segment tracking
  studyId: string; // Used for segment tracking
  studyType?: StudyType; // Determines which modals and UI to show
}

/*
 * RegionList component
 *
 * This component is used to display the list of regions detected in the media
 * It also provides the functionality to delete and update the classification of the region
 */
export const RegionList = ({ projectId, studyId, studyType = "asset" }: RegionListProps) => {
  /* Actions */
  const { deleteRegion, deleteAllRegions, setSelectedRegion, updateRegionLabel, addRegion } = useEditorStore((state) => state.editorActions);

  /* Stores */
  const { open, close } = useModalStore((state) => state.modalActions);
  const assetIndex = useEditorStore((state) => state.assetIndex);
  const allAssetRegionData = useEditorStore((state) => state.allAssetRegionData);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { canPaste: canPasteMulti, checkClipboard: checkClipboardForMulti } = useClipboardRegionCheck("multi");

  /* State */
  const selectedRegion = useEditorStore((state) => state.selectedRegion);
  const sidePanelDisplay = useEditorStore((state) => state.sidePanelDisplay);

  /* Consts */
  const isVHSet = sidePanelDisplay.some((region) => region.priority !== undefined && region.priority !== null && region.priority !== 0);

  /* Handlers */
  const handleDeleteRegion = (id: string, event: MouseEvent<HTMLDivElement>) => {
    // stop the event from propagating to the parent div
    event.stopPropagation();

    // find the region
    const region = sidePanelDisplay.find((region) => region.id === id);

    //Segment tracking
    segmentTrackEvent("AOI Removed", { classification: region?.classification, label: region?.label, projectId, studyId, studyType });
    deleteRegion(id);
  };

  const handleSelectRegion = (id: string) => {
    setSelectedRegion(id);
  };

  const handleUpdateRegionClassification = (region: Region, event: MouseEvent<HTMLDivElement>) => {
    // stop the event from propagating to the parent div
    event.stopPropagation();

    // Use scene modal for scene studies, asset modal for asset studies
    if (studyType === "scene") {
      open(<SceneColorPickerModal region={region} onClose={close} projectId={projectId} studyId={studyId} />, { className: "max-w-[244px] p-6" });
    } else {
      open(<UpdateRegionModal region={region} onClose={close} projectId={projectId} studyId={studyId} />, { className: "w-[340px] max-h-[516px]" });
    }
  };

  const handleSetVisualHierarchy = () => {
    // Segment tracking
    segmentTrackEvent("AOI Priority Editing Started", { projectId, studyId, studyType });
    open(<SetVisualHierarchyModal onClose={close} projectId={projectId} studyId={studyId} />, {
      className: "min-h-[80vh] h-[80vh] max-h-[80vh] min-w-[80vw] w-[80vw] max-w-[80vw] overflow-hidden",
    });
  };

  const handleRegionEdit = (regionId: string, newLabel: string) => {
    // find the region
    const region = sidePanelDisplay.find((region) => region.id === regionId);
    if (!region) return;

    // Ensure unique label
    const existingLabels = sidePanelDisplay.map((r) => r.label);
    const uniqueLabel = getUniqueLabel(newLabel, existingLabels, region.label);

    // Segment tracking
    segmentTrackEvent("AOI Label Changed", { from: region?.label, to: uniqueLabel, projectId, studyId, studyType });

    // update the region label
    updateRegionLabel(regionId, uniqueLabel);
  };

  const handleRemoveAllRegions = () => {
    segmentTrackEvent("Asset AOIs Cleared", { numOfAOIs: sidePanelDisplay.length, projectId, studyId, studyType });
    deleteAllRegions();
  };

  const handleCopyAll = async () => {
    // Don't copy if there are no regions to copy
    if (sidePanelDisplay.length === 0) return;
    try {
      // Write a multi-region payload to the clipboard
      await navigator.clipboard.writeText(JSON.stringify({ type: "multi", regions: sidePanelDisplay }));

      // Segment tracking
      segmentTrackEvent("AOIs Copied", { numOfAOIs: sidePanelDisplay.length, projectId, studyId, studyType, location: "region-list" });

      // Show success toast
      toast({ title: "AOIs copied to clipboard", type: "success" });
    } catch {
      toast({ title: "Failed to copy AOIs", type: "error" });
    }
  };

  const handlePasteAll = async () => {
    try {
      const text = await navigator.clipboard.readText();
      // Basic validation of the payload structure before processing
      if (!text) throw new Error("empty clipboard");
      const payload = JSON.parse(text);

      // Validate that it's a multi-region payload with the expected structure
      if (payload.type !== "multi" || !Array.isArray(payload.regions)) throw new Error("not a multi-region payload");

      // Get target asset metadata for clamping and size validation
      const targetMeta = allAssetRegionData[assetIndex]?.metaData;
      if (!targetMeta || payload.regions.length === 0) return;

      // Process the regions for pasting, which includes clamping to canvas bounds and ensuring unique labels
      const existingLabels = sidePanelDisplay.map((r) => r.label);
      const { regionsToAdd, skipped } = processRegionsForPaste(payload.regions as Region[], targetMeta, existingLabels);
      const pasted = regionsToAdd.length;

      // Add the processed regions to the store
      regionsToAdd.forEach((region) => addRegion(region));

      // Segment tracking
      segmentTrackEvent("AOIs Pasted", { numPasted: pasted, numSkipped: skipped, projectId, studyId, studyType, location: "region-list" });

      // Show feedback toast based on the results of the paste operation
      if (skipped > 0 && pasted === 0) {
        toast({ title: "AOIs were not pasted", description: "The AOIs are too large to fit in this asset.", type: "warning" });
      } else if (skipped > 0) {
        toast({ title: "AOIs were pasted but due to different asset sizes they might not appear in exact location", type: "warning" });
      } else {
        toast({ title: "AOIs pasted", type: "success" });
      }
    } catch {
      toast({ title: "Nothing to paste", type: "warning" });
    }
  };

  /* Render */
  return (
    <>
      <h2 className="font-semibold pb-2">Areas of interest (AOI)</h2>
      <p className="opacity-70">
        {studyType === "scene" ? "Add areas of interest (AOIs) within the scene:" : "We automatically detected areas of interest (AOIs) within the asset. You can edit AOIs here:"}
      </p>
      <div className="flex flex-col overflow-auto h-full pt-4">
        <div className="grid grid-cols-[76px_4fr_auto] rounded-t-lg bg-base-200 p-3 items-center justify-between">
          <p className="text-sm opacity-60 pl-3">Type</p>
          <p className="text-sm opacity-60 pl-3">Label</p>
          <CustomDropdown position="bottom" align="end" dropdownClassname="relative" triggerBtnClassname="p-1 hover:bg-base-300 rounded" onMouseEnter={checkClipboardForMulti}>
            <CustomDropdownItem onClick={handleCopyAll} className={cn("flex items-center gap-2 text-sm", sidePanelDisplay.length === 0 && "opacity-40 pointer-events-none")}>
              <DocumentTextIcon className="size-4 shrink-0" />
              Copy all
            </CustomDropdownItem>
            <CustomDropdownItem onClick={handlePasteAll} className={cn("flex items-center gap-2 text-sm", !canPasteMulti && "opacity-40 pointer-events-none")}>
              <ClipboardDocumentIcon className="size-4 shrink-0" />
              Paste all
            </CustomDropdownItem>
            <CustomDropdownItem
              onClick={handleRemoveAllRegions}
              className={cn("flex items-center gap-2 text-sm text-error", sidePanelDisplay.length === 0 && "opacity-40 pointer-events-none")}
            >
              <TrashIcon className="size-4 shrink-0" />
              Delete all
            </CustomDropdownItem>
          </CustomDropdown>
        </div>
        {/* Empty State */}
        {sidePanelDisplay.length === 0 && (
          <div className="flex flex-col items-center justify-center min-h-48 border-b-2 border-base-200">
            <p className="text-sm opacity-70">{studyType === "scene" ? "No AOIs in this scene yet..." : "No AOIs in this asset."}</p>
          </div>
        )}

        {/* Regions List */}
        {sidePanelDisplay &&
          sidePanelDisplay.map((region, index) => (
            <div
              key={`sidebar-right-${index}`}
              className={`grid grid-cols-[76px_4fr_1fr] p-3 group items-center justify-between ${region?.id === selectedRegion ? "border-2 border-primary rounded-lg bg-base-200" : "border-b-2 border-base-200"}`}
              onClick={() => handleSelectRegion(region.id)}
            >
              <div className="w-full flex items-center truncate cursor-pointer ml-3" onClick={(event) => handleUpdateRegionClassification(region, event)}>
                <div className="size-4 rounded-xs inline-block" style={{ backgroundColor: region?.color || classColors[region?.classification] }} />
                <ChevronDownIcon className="size-4 ml-2" />
              </div>
              <div className="flex items-center truncate">
                <EditElement defaultValue={region?.label} elementId={region?.id} handleElementEdit={handleRegionEdit}>
                  <p className="text-sm pl-3 truncate">{region?.label}</p>
                </EditElement>
              </div>
              <div className="justify-self-end">
                <div className="inline-block cursor-pointer" onClick={(event) => handleDeleteRegion(region?.id, event)}>
                  <TrashIcon className="size-4 mt-1" />
                </div>
              </div>
            </div>
          ))}

        {studyType === "asset" && (
          <div className="bg-base-200 p-4 rounded-2xl flex flex-row justify-center mt-4 gap-4">
            <img src={PAGraphic} alt="Priority alignment graphic" className="w-14 h-14" />
            <div className="flex flex-col justify-center gap-4">
              <p className="text-sm opacity-70">Assign a priority to your AOIs to see how attention is distributed within the asset.</p>
              <Button onClick={handleSetVisualHierarchy} className="w-fit text-sm font-semibold bg-base-100 text-base-content">
                <BarsArrowUpIcon className="size-3.5 inline-block mr-1" />
                {isVHSet ? "Edit priorities" : "Set priorities"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
